import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Check, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { gaEvent, CONVERSION_EVENTS } from "../lib/analytics";
import { api, SERVICES } from "../lib/api";
import { cn } from "../lib/utils";
import { useSEO } from "../lib/useSEO";

const TYPES = {
    discovery: {
        key: "discovery",
        label: "Complimentary Discovery Call",
        duration: "30 minutes",
        price: "Free",
        priceClass: "text-brand-champagne",
        description: "An introductory conversation to discuss your goals, challenges and how we might help. No obligation.",
        bullets: [
            "30-minute call",
            "Discussion of goals and challenges",
            "No obligation",
        ],
    },
    consultation: {
        key: "consultation",
        label: "Hospitality Operations Consultation",
        duration: "Up to 90 minutes",
        price: "From £250",
        priceClass: "text-brand-navy",
        description: "A focused consultation covering operational review and recommendations, with a follow-up summary report.",
        bullets: [
            "Up to 90 minutes, virtual or on-site",
            "Operational review and recommendations",
            "Follow-up summary report",
        ],
    },
};

export default function BookingPage() {
    useSEO({
        title: "Book a Consultation — Hospitality Consultant | Optimum Hospitality Solutions",
        description:
            "Book a complimentary 30-minute discovery call or a hospitality operations consultation (from £250). Hospitality consultant supporting hotels, serviced apartments, restaurants and bars.",
        path: "/booking",
    });

    const [params] = useSearchParams();
    const initialType = params.get("type") === "discovery" ? "discovery" : params.get("type") === "consultation" ? "consultation" : null;
    const isOpisTopic = params.get("topic") === "opis";

    const [type, setType] = useState(initialType);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        service: isOpisTopic ? "OPIS™ Founding Member Programme" : "",
        message: isOpisTopic
            ? "I would like to discuss whether my property is a fit for the OPIS™ Founding Member Programme."
            : "",
    });
    const [date, setDate] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (initialType && initialType !== type) setType(initialType);
        // `type` is intentionally omitted: we only react to URL query changes, not to local state changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialType]);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.service || !form.message) {
            toast.error("Please complete all required fields.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/bookings", {
                ...form,
                consultation_type: type,
                preferred_date: date ? format(date, "yyyy-MM-dd") : null,
            });
            gaEvent(CONVERSION_EVENTS.BOOKING_SUBMIT, {
                consultation_type: type || "unspecified",
                service: form.service,
            });
            setSubmitted(true);
            toast.success("Request received. We'll be in touch shortly.");
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again or email us directly.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div data-testid="booking-success" className="min-h-[60vh]">
                <div className="mx-auto max-w-3xl px-6 lg:px-12 py-24 text-center">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-champagne/20 text-brand-champagne mx-auto">
                        <Check size={28} strokeWidth={1.5} />
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl mt-8 text-brand-navy">
                        Thank you.
                    </h1>
                    <p className="mt-5 text-brand-ink/75 leading-relaxed">
                        Your {type === "discovery" ? "discovery call request" : "consultation request"} has been received. We typically respond within one business day to schedule a call and confirm next steps.
                    </p>
                    <button
                        onClick={() => {
                            setSubmitted(false);
                            setForm({ name: "", email: "", phone: "", service: "", message: "" });
                            setDate(null);
                            setType(null);
                        }}
                        data-testid="booking-submit-another"
                        className="mt-10 inline-flex items-center px-6 py-3 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors"
                    >
                        Submit another request
                    </button>
                </div>
            </div>
        );
    }

    // STEP 1 — Consultation type selection
    if (!type) {
        return (
            <div data-testid="booking-page-type-select">
                <section className="border-b border-brand-border">
                    <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Consultation
                        </span>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                            How would you like<br/>to begin?
                        </h1>
                        <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                            Choose between a complimentary discovery call or a structured operations consultation. Every engagement is shaped around your business. A discovery call is the simplest place to start.
                        </p>
                    </div>
                </section>

                <section>
                    <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-px bg-brand-border">
                        {/* Discovery card */}
                        <button
                            type="button"
                            data-testid="select-type-discovery"
                            onClick={() => setType("discovery")}
                            className="text-left bg-brand-navy text-brand-stone p-12 hover:brightness-110 transition-all group"
                        >
                            <div className="text-xs uppercase tracking-[0.25em] text-brand-champagne">
                                Recommended starting point
                            </div>
                            <h2 className="font-display text-3xl mt-4 text-brand-stone">
                                Complimentary Discovery Call
                            </h2>
                            <div className="mt-6 flex items-baseline gap-3">
                                <span className="font-display text-5xl text-brand-champagne">Free</span>
                                <span className="text-sm text-brand-stone/60">· 30 minutes</span>
                            </div>
                            <ul className="mt-8 space-y-3 text-sm text-brand-stone/85 leading-relaxed">
                                {TYPES.discovery.bullets.map((b) => (
                                    <li key={b} className="flex gap-3"><span className="text-brand-champagne">—</span>{b}</li>
                                ))}
                            </ul>
                            <div className="mt-10 inline-flex items-center gap-2 text-brand-champagne text-sm tracking-wide group-hover:translate-x-1 transition-transform">
                                Book a free discovery call
                                <ArrowRight size={16} />
                            </div>
                        </button>

                        {/* Consultation card */}
                        <button
                            type="button"
                            data-testid="select-type-consultation"
                            onClick={() => setType("consultation")}
                            className="text-left bg-brand-stone p-12 hover:bg-white transition-colors group"
                        >
                            <div className="text-xs uppercase tracking-[0.25em] text-brand-champagne">
                                Structured engagement
                            </div>
                            <h2 className="font-display text-3xl mt-4 text-brand-navy">
                                Hospitality Operations Consultation
                            </h2>
                            <div className="mt-6 flex items-baseline gap-3">
                                <span className="text-xs uppercase tracking-[0.15em] text-brand-ink/60">Investment</span>
                                <span className="font-display text-4xl text-brand-navy">From £250</span>
                            </div>
                            <ul className="mt-8 space-y-3 text-sm text-brand-ink/75 leading-relaxed">
                                {TYPES.consultation.bullets.map((b) => (
                                    <li key={b} className="flex gap-3"><span className="text-brand-champagne">—</span>{b}</li>
                                ))}
                            </ul>
                            <div className="mt-10 inline-flex items-center gap-2 text-brand-navy text-sm tracking-wide group-hover:translate-x-1 transition-transform">
                                Request a consultation
                                <ArrowRight size={16} />
                            </div>
                        </button>
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-12 pb-16">
                        <p className="text-sm text-brand-ink/65 italic max-w-3xl">
                            All consulting engagements are tailored to the specific needs of each client. Following a discovery call, a bespoke proposal will be provided where appropriate.
                        </p>
                    </div>
                </section>
            </div>
        );
    }

    const selected = TYPES[type];

    // STEP 2 — Form
    return (
        <div data-testid="booking-page">
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <button
                        type="button"
                        onClick={() => setType(null)}
                        data-testid="booking-change-type"
                        className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium hover:text-brand-navy"
                    >
                        ← Change consultation type
                    </button>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                        {selected.label}.
                    </h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                        {selected.description}
                    </p>
                </div>
            </section>

            <section>
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <aside className="lg:col-span-4">
                        <div className="border border-brand-border p-8 bg-white">
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                {selected.label}
                            </div>
                            <div className="mt-4 flex items-baseline gap-2">
                                <span className={`font-display text-3xl ${selected.priceClass}`}>
                                    {selected.price}
                                </span>
                                <span className="text-sm text-brand-ink/60">· {selected.duration}</span>
                            </div>
                            <ul className="mt-6 space-y-3 text-sm text-brand-ink/80 leading-relaxed">
                                {selected.bullets.map((b) => (
                                    <li key={b} className="flex gap-3"><span className="text-brand-champagne">—</span>{b}</li>
                                ))}
                            </ul>
                            <div className="mt-8 pt-6 border-t border-brand-border">
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">Next steps</div>
                                <ol className="mt-4 space-y-3 text-xs text-brand-ink/75 leading-relaxed">
                                    <li className="flex gap-3"><span className="text-brand-champagne">01</span>Submit this form</li>
                                    <li className="flex gap-3"><span className="text-brand-champagne">02</span>We confirm a time within one business day</li>
                                    <li className="flex gap-3"><span className="text-brand-champagne">03</span>Bespoke proposal where appropriate</li>
                                </ol>
                            </div>
                        </div>
                    </aside>

                    <form
                        onSubmit={handleSubmit}
                        data-testid="booking-form"
                        className="lg:col-span-8 border border-brand-border p-8 lg:p-12 bg-white space-y-7"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <Label htmlFor="b-name" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                    Full name
                                </Label>
                                <Input
                                    id="b-name"
                                    data-testid="booking-input-name"
                                    value={form.name}
                                    onChange={(e) => set("name", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="b-email" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                    Email
                                </Label>
                                <Input
                                    id="b-email"
                                    type="email"
                                    data-testid="booking-input-email"
                                    value={form.email}
                                    onChange={(e) => set("email", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="b-phone" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                    Phone
                                </Label>
                                <Input
                                    id="b-phone"
                                    data-testid="booking-input-phone"
                                    value={form.phone}
                                    onChange={(e) => set("phone", e.target.value)}
                                    className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                    required
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                    Service area of interest
                                </Label>
                                <Select
                                    value={form.service}
                                    onValueChange={(v) => set("service", v)}
                                >
                                    <SelectTrigger
                                        data-testid="booking-select-service"
                                        className="mt-2 rounded-none border-brand-border focus:ring-brand-navy"
                                    >
                                        <SelectValue placeholder="Select a service area" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SERVICES.map((s) => (
                                            <SelectItem
                                                key={s}
                                                value={s}
                                                data-testid={`booking-service-option-${s.toLowerCase().replace(/\s+/g, "-")}`}
                                            >
                                                {s}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                Preferred date (optional)
                            </Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        data-testid="booking-date-trigger"
                                        className={cn(
                                            "mt-2 w-full justify-start rounded-none border-brand-border bg-white font-normal hover:bg-brand-stone",
                                            !date && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-3 h-4 w-4 text-brand-champagne" />
                                        {date ? format(date, "PPP") : "Pick a date"}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-auto p-0"
                                    align="start"
                                    data-testid="booking-date-popover"
                                >
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={setDate}
                                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        <div>
                            <Label htmlFor="b-message" className="text-xs uppercase tracking-[0.2em] text-brand-ink/70">
                                Tell us about your hospitality business and objectives
                            </Label>
                            <Textarea
                                id="b-message"
                                data-testid="booking-input-message"
                                value={form.message}
                                onChange={(e) => set("message", e.target.value)}
                                rows={5}
                                className="mt-2 rounded-none border-brand-border focus-visible:ring-brand-navy"
                                required
                            />
                        </div>

                        <Button
                            type="submit"
                            data-testid="booking-submit-button"
                            disabled={submitting}
                            className="rounded-none bg-brand-navy text-brand-stone hover:bg-brand-hover px-8 py-6 text-sm tracking-wide"
                        >
                            {submitting ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Sending...
                                </span>
                            ) : type === "discovery" ? (
                                "Request discovery call"
                            ) : (
                                "Request consultation"
                            )}
                        </Button>
                    </form>
                </div>
            </section>
        </div>
    );
}
